/**
 * Visual e-signature field editor: shows the document's pages and, for every
 * field, highlights the words it is attached to and draws the element where the
 * signing step will stamp it. Dragging an element writes its X / Y offsets,
 * dragging its right edge writes its width. Ported from the legacy
 * html/assets-ftp/js/esign-placement-editor.js (FTP repo) by way of the
 * corporate dashboard's port, corp-dashboard/components/esign/placement-editor-dialog.tsx,
 * whose look and behaviour this copies; the legacy behaviour test
 * (tests/esign/editor-behaviour.js) is mirrored in PlacementEditorDialog.test.tsx.
 *
 * Coordinates follow lib/esign/geometry.ts (a port of the server's
 * KeywordPlacement): anchors are bottom-left PDF points, offsets are points
 * added without page scaling (+Y down), width is millimetres, and a page is
 * drawn 210 mm wide.
 *
 * Matching happens twice: instantly in the browser against the document's
 * text runs (same rule as the server), and on the server across every page and
 * document. A server answer is trusted only for the exact words it was
 * computed for.
 *
 * Prospera plumbing, where it differs from corp:
 *  - the API calls are services/documentLibrary.service (no account argument:
 *    the request's own account);
 *  - the shell is Prospera's Modal, opened on top of the document / packet
 *    modal. That Modal is told not to close on Escape, because every open
 *    Modal hears the key; Escape is handled with the arrow keys below instead;
 *  - corp's window.confirm questions are Prospera's ConfirmDialog;
 *  - `readOnly` (a Locked Admin page): the pages, highlights and elements show
 *    and a field can be selected to see its details, but nothing changes: no
 *    add / remove / pick, no dragging, inputs off, Close only. Each guard is
 *    marked "readOnly:" below.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, MousePointerClick, Plus, RotateCcw, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { DialogHeading } from '@/components/admin/DialogChrome';
import { PdfPageView, loadPdf, type PdfHandle, type PdfTextItem } from '@/components/admin/esign/PdfPageView';
import { getEsignAnchors, getEsignFields, getEsignPreviewInfo, getEsignTextRuns, saveEsignFields } from '@/services/documentLibrary.service';
import type { AnchorHit, EsignField, EsignFieldInput, FieldOwnerType, PreviewDoc, TextRun } from '@/services/documentLibrary.service';
import { FIELD_TYPE_OPTIONS, fieldTypeLabel } from '@/services/documentLibrary.service';
import { cancellable, isAbortError } from '@/lib/cancellable';
import { colourAlpha, fieldColour } from '@/lib/esign/fields';
import {
  TEXT_FONT_MM,
  TEXT_LINE_MM,
  dragDeltaPxToPt,
  elementSizeMm,
  freePlaceAnchor,
  freePlaceDefaultOffsetPt,
  geometryForRun,
  isSignatureType,
  keywordIsFreePlaced,
  keywordWarnings,
  offsetValue,
  pageMetrics,
  pageNumberOf,
  placementMm,
  pxPerMm,
  pxToMm,
  roundOffsetPt,
  roundWidthMm,
  runRectPx,
  signatureWidthMm,
} from '@/lib/esign/geometry';
import { phpTrim, runMatchesKeyword } from '@/lib/esign/match';

export interface PlacementEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerType: FieldOwnerType;
  ownerId: number;
  ownerTitle: string;
  /** Documents only: preview this file (for example one uploaded but not saved yet) instead of the saved one. */
  fileName?: string;
  /** Open with this field selected. */
  initialFieldId?: number;
  /** Called after a successful save with the owner's fields as stored. */
  onSaved: (fields: EsignField[]) => void;
  /** View only (a Locked Admin page): show the fields, change nothing, Close only. */
  readOnly?: boolean;
}

const ANCHOR_DEBOUNCE_MS = 300;
const TETHER_MIN_PX = 6;
const MIN_RESIZE_MM = 5;
const DEFAULT_RENDER_WIDTH_PX = 800;
/** A preview link is valid for 5 minutes; ask for a new one once it is older than this. */
export const PREVIEW_URL_MAX_AGE_MS = 4 * 60 * 1000;

export const PLACEMENT_COPY = {
  loadFailed: 'The fields could not be loaded. Close this window and try again. If it keeps happening, ask your administrator.',
  preparing: 'Preparing the preview…',
  previewFailed: 'The preview could not be shown. Try again, and if it keeps happening ask your administrator.',
  noRows: 'No fields yet.',
  noFile: 'This document has no file yet. Upload one on the Documents page.',
  missingFile: 'The file for this document is no longer stored. Upload it again on the Documents page.',
  entryNoFile: 'This document has no file uploaded. Upload it on the Documents page first.',
  noDocs: 'Add a document to this packet first, then position the signature fields.',
  unsupported: 'This file type can’t be shown here. Upload the document as a PDF or Word (.docx) file to position fields.',
  convert: 'This document could not be prepared for viewing. Try again, and if it keeps happening ask your administrator.',
  unavailable: 'The preview isn’t available right now. Try again in a minute.',
  unreadable: 'The pages of this file could not be read. Open it, save it again as a PDF, and upload it on the Documents page.',
  cannotBeSigned:
    'This file can be shown here, but it cannot be signed as it is. Open it, save it again as a PDF, and upload it on the Documents page.',
  wordsLabel: 'Words on the document',
  wordsPlaceholder: 'e.g. Patient Signature',
  pick: 'Pick from document',
  pickBar: 'Click the words on the page to use them.',
  pickBarEmpty: 'No readable text on this page. Type the words instead.',
  pickBarLoading: 'Reading the page…',
  notFoundDoc: 'These words are not in this document. Check the spelling or pick them from the document.',
  notFoundPacket: 'These words are not in any document of this packet. Check the spelling or pick them from the document.',
  warnShortcode:
    'This looks like a merge field. Merge fields are replaced before the patient sees the document, so they are never found. Use the printed words next to it.',
  warnWhitespace: 'Has spaces at the start or end.',
  badgeNeedsWords: 'Needs words',
  badgeNotFound: 'Not found',
  badgeHere: 'On this page',
  badgeOtherDoc: 'Other document',
  badgeFixedHere: 'Fixed spot',
  badgeFixedElsewhere: 'Fixed spot on page ',
  badgeFixedMissing: 'Page is gone',
  fixedMissingPage:
    'This document only has {count} pages now. Move this field to a page that exists, or it will not appear when the patient signs.',
  fixedNeedsPdf: 'A fixed spot needs a PDF. Save this document as a PDF and upload it again on the Documents page.',
  fixedNeedsPreview: 'This page could not be shown, so a fixed spot can’t be set. Try again, and if it keeps happening ask your administrator.',
  fixedNotInPackets: 'Fixed spots aren’t available in packets, because the pages are combined when the packet is sent.',
  fixedClearsWords: 'Switch this field to a fixed spot on page {page}? The words on it will be removed. You can type them again if you switch back.',
  removeConfirm: 'Remove this field? Nothing will be placed for it once you save.',
  placeLabel: 'Where it goes',
  placeByWords: 'Words on the document',
  placeFixed: 'A fixed spot on this page',
  leaveDocument: "Leave without saving? The changes to this document's fields will be discarded.",
  leavePacket: "Leave without saving? The changes to this packet's fields will be discarded.",
  saveFailed: 'The fields could not be saved. Check your connection and try again.',
  // Prospera only: the ConfirmDialog that asks the questions above needs a title and a button.
  removeTitle: 'Remove field?',
  removeAction: 'Remove field',
  fixedClearsWordsTitle: 'Use a fixed spot?',
  fixedClearsWordsAction: 'Use a fixed spot',
  leaveTitle: 'Discard changes?',
  leaveAction: 'Discard changes',
} as const;

const SAMPLE_TEXT: Record<string, () => string> = {
  date: () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  },
  name: () => 'Jane Doe',
  shorttext: () => 'Sample text',
  longtext: () => 'Sample text',
};

/** One field as the editor holds it. `key` is stable for the life of the dialog. */
interface Row {
  key: string;
  id: number | null;
  title: string;
  tabKeywords: string;
  keywordType: string;
  signatureX: string;
  signatureY: string;
  signaturePageNum: string;
  width: string;
  optional: boolean;
}

interface PageSize {
  page: number;
  widthMm: number;
  heightMm: number;
}

interface Anchor {
  docIndex: number;
  page: number;
  xPt: number;
  yPt: number;
  text: string;
  free?: boolean;
}

type StatusState = 'pending' | 'notext' | 'nopreview' | 'here' | 'notfound' | 'elsewhere' | 'fixedhere' | 'fixedelsewhere' | 'fixedmissing';

interface FieldStatus {
  state: StatusState;
  badge: string;
  badgeTitle: string;
  line: string;
  warns: Array<'shortcode' | 'whitespace'>;
  banner: string;
  jump: { docIndex: number; page: number } | null;
}

interface LiveEdit {
  kind: 'move' | 'resize';
  key: string;
  index: number;
  dxPx: number;
  dyPx: number;
}

interface DragStart {
  kind: 'move' | 'resize';
  key: string;
  index: number;
  clientX: number;
  clientY: number;
  startX: number;
  startY: number;
  startW: number;
}

/** A question corp asked with window.confirm, asked here with ConfirmDialog. */
interface Ask {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'danger' | 'warning';
  onConfirm: () => void;
}

function rowFromField(field: EsignField): Row {
  return {
    key: `f${field.documentKeywordID}`,
    id: field.documentKeywordID,
    title: field.title ?? '',
    tabKeywords: field.tabKeywords ?? '',
    keywordType: field.keywordType ?? '',
    signatureX: String(field.signatureX ?? '0'),
    signatureY: String(field.signatureY ?? '0'),
    signaturePageNum: String(field.signaturePageNum ?? ''),
    width: field.width === null || field.width === undefined ? '' : String(field.width),
    optional: !!field.optional,
  };
}

function rowToInput(row: Row): EsignFieldInput {
  return {
    ...(row.id ? { documentKeywordID: row.id } : {}),
    title: row.title,
    tabKeywords: row.tabKeywords,
    keywordType: row.keywordType,
    signatureX: row.signatureX,
    signatureY: row.signatureY,
    signaturePageNum: row.signaturePageNum,
    width: row.width,
    optional: row.optional,
  };
}

function isFixed(row: Row): boolean {
  return keywordIsFreePlaced(row.tabKeywords, row.signaturePageNum);
}

function isPdfName(fileName: string | null | undefined): boolean {
  return /\.pdf$/i.test((fileName ?? '').trim());
}

function displayTitle(row: Row): string {
  return row.title.trim() || 'Field';
}

// corp's `shadow-xs` is Tailwind 4; Tailwind 3's `shadow-sm` is the same shadow.
const selectClass =
  'h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50';

export function PlacementEditorDialog({
  open,
  onOpenChange,
  ownerType,
  ownerId,
  ownerTitle,
  fileName,
  initialFieldId,
  onSaved,
  readOnly = false,
}: PlacementEditorDialogProps) {
  const isPacket = ownerType === 'packet';

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [savedRows, setSavedRows] = useState<Row[]>([]);
  const [loadedIds, setLoadedIds] = useState<number[]>([]);
  const [docs, setDocs] = useState<PreviewDoc[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [viewerPages, setViewerPages] = useState<Record<number, PageSize[]>>({});
  const [docIndex, setDocIndex] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pdf, setPdf] = useState<PdfHandle | null>(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [runsByDoc, setRunsByDoc] = useState<Record<string, Record<number, TextRun[]>>>({});
  const [itemsByPage, setItemsByPage] = useState<Record<string, PdfTextItem[]>>({});
  const [serverAnchors, setServerAnchors] = useState<{ hits: Record<string, AnchorHit[]>; text: Record<string, string> }>({
    hits: {},
    text: {},
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [pickKey, setPickKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [placeMessage, setPlaceMessage] = useState<{ key: string; text: string } | null>(null);
  const [live, setLive] = useState<LiveEdit | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  /** When each document's preview info arrived (Date.now()), by index. */
  const [fetchedAt, setFetchedAt] = useState<Record<number, number>>({});
  /** A document whose preview info could not be refreshed, by index. */
  const [docErrors, setDocErrors] = useState<Record<number, string>>({});
  /** The question on screen (ConfirmDialog), if any. */
  const [ask, setAsk] = useState<Ask | null>(null);

  const sessionRef = useRef<AbortController | null>(null);
  const runsRequested = useRef<Set<string>>(new Set());
  const itemsRequested = useRef<Set<string>>(new Set());
  const anchorSeq = useRef(0);
  const newKeySeq = useRef(0);
  const dragRef = useRef<DragStart | null>(null);
  const wordsInputRef = useRef<HTMLInputElement | null>(null);
  const focusWordsNext = useRef(false);
  const refreshing = useRef<Set<number>>(new Set());
  const pdfRetried = useRef<Set<number>>(new Set());

  // ---- Load fields and preview info when the dialog opens -------------------
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const { signal } = controller;
    sessionRef.current = controller;
    runsRequested.current = new Set();
    itemsRequested.current = new Set();
    refreshing.current = new Set();
    pdfRetried.current = new Set();
    anchorSeq.current++;

    setLoadError(null);
    setRows([]);
    setSavedRows([]);
    setLoadedIds([]);
    setDocs([]);
    setPreviewError(null);
    setViewerPages({});
    setDocIndex(0);
    setPageNum(1);
    setRunsByDoc({});
    setItemsByPage({});
    setServerAnchors({ hits: {}, text: {} });
    setStatusMessage(null);
    setSelectedKey(null);
    setPickKey(null);
    setEditing({});
    setPlaceMessage(null);
    setLive(null);
    setSaving(false);
    setSaveError(null);
    setFetchedAt({});
    setDocErrors({});
    setAsk(null);

    const load = async () => {
      try {
        setLoading(true);
        const [fieldsRes, previewRes] = await Promise.all([
          cancellable(signal, () => getEsignFields(ownerType, ownerId)),
          cancellable(signal, () => getEsignPreviewInfo(ownerType, ownerId, fileName)),
        ]);
        if (!fieldsRes.ok) {
          setLoadError(fieldsRes.message);
          return;
        }
        const loaded = (fieldsRes.data?.keywords ?? []).map(rowFromField);
        setRows(loaded);
        setSavedRows(loaded);
        setLoadedIds(loaded.map((r) => r.id).filter((id): id is number => !!id));
        const initial = initialFieldId ? loaded.find((r) => r.id === initialFieldId) : undefined;
        setSelectedKey((initial ?? loaded[0])?.key ?? null);

        if (previewRes.ok) {
          const list = previewRes.data?.docs ?? [];
          const now = Date.now();
          setDocs(list);
          setFetchedAt(Object.fromEntries(list.map((_, i) => [i, now])));
          const first = list.findIndex((d) => d.previewable || d.reason === 'pending');
          setDocIndex(first >= 0 ? first : 0);
        } else {
          setPreviewError(previewRes.message);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        setLoadError(PLACEMENT_COPY.loadFailed);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => {
      controller.abort();
      if (sessionRef.current === controller) sessionRef.current = null;
    };
  }, [open, ownerType, ownerId, fileName, initialFieldId]);

  const currentDoc: PreviewDoc | null = docs[docIndex] ?? null;
  const currentDocUrl = currentDoc?.previewable ? currentDoc.url : null;
  const currentDocKey = currentDoc?.previewable ? currentDoc.key : null;
  const currentDocHasPages = !!currentDoc && currentDoc.pages.length > 0;
  const currentDocPending = currentDoc?.reason === 'pending';
  const currentDocId = currentDoc?.documentID ?? null;
  const currentFetchedAt = fetchedAt[docIndex] ?? 0;
  const currentDocError = docErrors[docIndex] ?? null;

  // ---- Ask again for one document's preview info ------------------------------
  // A packet member is prepared only when asked for, and every link expires, so
  // the document about to be shown may need a fresh answer first.
  const refreshDoc = useCallback(
    async (index: number, documentId: number | null) => {
      const session = sessionRef.current;
      if (!session || refreshing.current.has(index)) return;
      refreshing.current.add(index);
      setDocErrors((prev) => {
        if (!(index in prev)) return prev;
        const next = { ...prev };
        delete next[index];
        return next;
      });
      const fail = (message: string) => setDocErrors((prev) => ({ ...prev, [index]: message }));
      try {
        const res = await cancellable(session.signal, () =>
          isPacket ? getEsignPreviewInfo(ownerType, ownerId, undefined, documentId ?? undefined) : getEsignPreviewInfo(ownerType, ownerId, fileName),
        );
        if (!res.ok) {
          fail(res.message);
          return;
        }
        const list = res.data?.docs ?? [];
        const found = isPacket ? (list.find((d) => d.documentID === documentId) ?? list[index]) : list[0];
        if (!found) {
          fail(PLACEMENT_COPY.previewFailed);
          return;
        }
        // Asked for by name and still not ready: say so instead of asking forever.
        const fresh: PreviewDoc = found.reason === 'pending' ? { ...found, previewable: false, reason: 'unavailable' } : found;
        setDocs((prev) => prev.map((d, i) => (i === index ? { ...fresh, index: d.index } : d)));
        setViewerPages((prev) => {
          if (!(index in prev)) return prev;
          const next = { ...prev };
          delete next[index];
          return next;
        });
        setFetchedAt((prev) => ({ ...prev, [index]: Date.now() }));
      } catch (error) {
        if (isAbortError(error)) return;
        fail(PLACEMENT_COPY.previewFailed);
      } finally {
        refreshing.current.delete(index);
      }
    },
    [ownerType, ownerId, fileName, isPacket],
  );

  // ---- Load the PDF of the document shown ------------------------------------
  useEffect(() => {
    setPdf(null);
    setPdfFailed(false);
    if (!open || currentDocError) return;
    const index = docIndex;
    if (currentDocPending) {
      refreshDoc(index, currentDocId);
      return;
    }
    if (!currentDocUrl) return;
    if (Date.now() - currentFetchedAt > PREVIEW_URL_MAX_AGE_MS) {
      refreshDoc(index, currentDocId);
      return;
    }
    let cancelled = false;
    let handle: PdfHandle | null = null;

    loadPdf(currentDocUrl).then(
      async (h) => {
        if (cancelled) {
          h.destroy();
          return;
        }
        handle = h;
        pdfRetried.current.delete(index);
        if (!currentDocHasPages) {
          // The server could not measure this file; pdf.js has just read it,
          // so take the page sizes from it.
          const sizes = await Promise.all(
            Array.from({ length: h.numPages }, (_, i) =>
              h.pageSizeMm(i + 1).then(
                (s) => ({ page: i + 1, widthMm: s.widthMm, heightMm: s.heightMm }),
                () => null,
              ),
            ),
          );
          if (cancelled) return;
          setViewerPages((prev) => ({ ...prev, [index]: sizes.filter((s): s is PageSize => s !== null) }));
        }
        setPdf(h);
      },
      () => {
        if (cancelled) return;
        // The link may have expired or the file moved: ask for it again once before giving up.
        if (!pdfRetried.current.has(index)) {
          pdfRetried.current.add(index);
          refreshDoc(index, currentDocId);
          return;
        }
        setPdfFailed(true);
      },
    );

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [open, docIndex, currentDocUrl, currentDocHasPages, currentDocPending, currentDocId, currentFetchedAt, currentDocError, refreshDoc]);

  // ---- Text runs of the document shown: one request per document -------------
  useEffect(() => {
    if (!open || !currentDocKey || runsRequested.current.has(currentDocKey)) return;
    const session = sessionRef.current;
    if (!session) return;
    const key = currentDocKey;
    runsRequested.current.add(key);
    const run = async () => {
      try {
        const res = await cancellable(session.signal, () => getEsignTextRuns(key));
        const pages: Record<number, TextRun[]> = {};
        if (res.ok) {
          for (const [p, list] of Object.entries(res.data?.pages ?? {})) pages[parseInt(p, 10)] = list ?? [];
        } else {
          setStatusMessage(res.message);
        }
        setRunsByDoc((prev) => ({ ...prev, [key]: pages }));
      } catch (error) {
        if (isAbortError(error)) return;
        setRunsByDoc((prev) => ({ ...prev, [key]: {} }));
      }
    };
    run();
  }, [open, currentDocKey]);

  // ---- pdf.js text items of the page shown (sizes of highlights) -------------
  useEffect(() => {
    if (!pdf || !currentDocKey) return;
    const cacheKey = `${currentDocKey}:${pageNum}`;
    if (itemsRequested.current.has(cacheKey)) return;
    itemsRequested.current.add(cacheKey);
    let cancelled = false;
    let settled = false;
    const store = (items: PdfTextItem[]) => {
      settled = true;
      if (!cancelled) setItemsByPage((prev) => ({ ...prev, [cacheKey]: items }));
    };
    pdf.textItems(pageNum).then(store, () => store([]));
    return () => {
      cancelled = true;
      // Not answered before the page changed: ask again next time it is shown.
      if (!settled) itemsRequested.current.delete(cacheKey);
    };
  }, [pdf, currentDocKey, pageNum]);

  // ---- Server matches, debounced and sequence-guarded -----------------------
  const wordsSignature = useMemo(() => JSON.stringify(rows.map((r) => [r.key, r.tabKeywords])), [rows]);
  const previewKeysSignature = useMemo(() => JSON.stringify(docs.map((d) => (d.reason === 'pending' ? '' : (d.key ?? '')))), [docs]);

  useEffect(() => {
    if (!open) return;
    const previewKeys = JSON.parse(previewKeysSignature) as string[];
    if (!previewKeys.some((k) => k !== '')) return;
    const list = JSON.parse(wordsSignature) as Array<[string, string]>;
    const timer = window.setTimeout(async () => {
      const seq = ++anchorSeq.current;
      if (list.length === 0) {
        setServerAnchors({ hits: {}, text: {} });
        return;
      }
      const session = sessionRef.current;
      if (!session) return;
      const keys = list.map(([k]) => k);
      const texts = list.map(([, t]) => t);
      try {
        const res = await cancellable(session.signal, () => getEsignAnchors(previewKeys, keys, texts));
        if (seq !== anchorSeq.current) return;
        if (!res.ok) {
          setStatusMessage(res.message);
          return;
        }
        setServerAnchors((prev) => {
          const hits = { ...prev.hits };
          const text = { ...prev.text };
          list.forEach(([k, t]) => {
            hits[k] = res.data?.anchors?.[k] ?? [];
            text[k] = t;
          });
          return { hits, text };
        });
      } catch (error) {
        if (isAbortError(error)) return;
      }
    }, ANCHOR_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, wordsSignature, previewKeysSignature]);

  // ---- Derived page state ----------------------------------------------------
  const pagesFor = useCallback(
    (index: number): PageSize[] => {
      const doc = docs[index];
      if (!doc) return [];
      return doc.pages.length ? doc.pages : (viewerPages[index] ?? []);
    },
    [docs, viewerPages],
  );
  const pages = pagesFor(docIndex);
  const pageCount = pages.length;
  const pageInfo: PageSize | null = currentDoc?.previewable && pdf ? (pages[pageNum - 1] ?? null) : null;
  const pagesFromViewer = !!currentDoc?.previewable && !currentDocHasPages && !!viewerPages[docIndex];
  const runsHere: TextRun[] | null = currentDocKey && runsByDoc[currentDocKey] ? (runsByDoc[currentDocKey][pageNum] ?? []) : null;
  const itemsHere: PdfTextItem[] | null = currentDocKey ? (itemsByPage[`${currentDocKey}:${pageNum}`] ?? null) : null;
  const anyPreviewable = docs.some((d) => d.previewable);

  const renderWidthPx = stageWidth > 0 ? Math.max(200, stageWidth - 24) : DEFAULT_RENDER_WIDTH_PX;
  const px = pxPerMm(renderWidthPx);
  const pageHeightPx = pageInfo ? pageMetrics(pageInfo.widthMm, pageInfo.heightMm).scaledPageHeightMm * px : 0;

  const stageObserver = useRef<ResizeObserver | null>(null);
  const stageRef = useCallback((el: HTMLDivElement | null) => {
    stageObserver.current?.disconnect();
    stageObserver.current = null;
    if (!el) return;
    const measure = () => setStageWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      stageObserver.current = ro;
    }
  }, []);

  // ---- Matching --------------------------------------------------------------
  const serverIsFresh = useCallback(
    (row: Row) => Object.prototype.hasOwnProperty.call(serverAnchors.text, row.key) && serverAnchors.text[row.key] === row.tabKeywords,
    [serverAnchors],
  );

  const localAnchorsHere = useCallback(
    (row: Row): Anchor[] =>
      (runsHere ?? [])
        .filter((run) => runMatchesKeyword(run.text, row.tabKeywords))
        .map((run) => ({ docIndex, page: pageNum, xPt: run.xPt, yPt: run.yPt, text: run.text })),
    [runsHere, docIndex, pageNum],
  );

  const serverAnchorsHere = useCallback(
    (row: Row): Anchor[] => (serverAnchors.hits[row.key] ?? []).filter((a) => a.docIndex === docIndex && a.page === pageNum),
    [serverAnchors, docIndex, pageNum],
  );

  /** What to draw: a fixed spot's corner anchor, else the server's answer for the current words, else the instant local match. */
  const effectiveAnchorsHere = useCallback(
    (row: Row): Anchor[] => {
      if (isFixed(row)) {
        if (!pageInfo || pageNumberOf(row.signaturePageNum) !== pageNum) return [];
        const a = freePlaceAnchor(pageInfo.heightMm);
        return [{ docIndex, page: pageNum, xPt: a.xPt, yPt: a.yPt, text: '', free: true }];
      }
      if (serverIsFresh(row)) return serverAnchorsHere(row);
      return localAnchorsHere(row);
    },
    [pageInfo, pageNum, docIndex, serverIsFresh, serverAnchorsHere, localAnchorsHere],
  );

  const docTitle = useCallback((index: number) => docs[index]?.title || `Document ${index + 1}`, [docs]);

  const statusFor = useCallback(
    (row: Row): FieldStatus => {
      const text = row.tabKeywords;
      const warns = keywordWarnings(text).filter((w): w is 'shortcode' | 'whitespace' => w !== 'empty');
      const st: FieldStatus = { state: 'pending', badge: '', badgeTitle: '', line: '', warns, banner: '', jump: null };

      if (isFixed(row)) {
        const page = pageNumberOf(row.signaturePageNum);
        st.warns = [];
        if (pageCount > 0 && page > pageCount) {
          st.state = 'fixedmissing';
          st.badge = PLACEMENT_COPY.badgeFixedMissing;
          st.banner = PLACEMENT_COPY.fixedMissingPage.replace('{count}', String(pageCount));
          return st;
        }
        if (page === pageNum) {
          st.state = 'fixedhere';
          st.badge = PLACEMENT_COPY.badgeFixedHere;
        } else {
          st.state = 'fixedelsewhere';
          st.badge = PLACEMENT_COPY.badgeFixedElsewhere + page;
          st.jump = { docIndex, page };
        }
        return st;
      }

      if (phpTrim(text) === '') {
        st.state = 'notext';
        st.badge = PLACEMENT_COPY.badgeNeedsWords;
        st.warns = [];
        st.banner = `“${displayTitle(row)}” has no words yet. Type the words it goes next to, or use Pick from document.`;
        return st;
      }

      if (!anyPreviewable) {
        st.state = 'nopreview';
        return st;
      }

      const localHere = localAnchorsHere(row);
      if (!serverIsFresh(row)) {
        if (localHere.length) {
          st.state = 'here';
          st.badge = PLACEMENT_COPY.badgeHere;
          st.line = localHere.length > 1 ? `Found ${localHere.length} times, all move together` : '';
        }
        return st; // still checking the rest of the document: no red badge while typing
      }

      const list = serverAnchors.hits[row.key] ?? [];
      if (!list.length) {
        st.state = 'notfound';
        st.badge = PLACEMENT_COPY.badgeNotFound;
        st.banner = docs.length > 1 ? PLACEMENT_COPY.notFoundPacket : PLACEMENT_COPY.notFoundDoc;
        return st;
      }

      const places = new Map<string, { docIndex: number; page: number; n: number }>();
      for (const a of list) {
        const k = `${a.docIndex}:${a.page}`;
        const place = places.get(k) ?? { docIndex: a.docIndex, page: a.page, n: 0 };
        place.n++;
        places.set(k, place);
      }
      const order = [...places.values()];
      const multiDoc = docs.length > 1;
      const pageWords = order.map((pl) => (multiDoc ? `${docTitle(pl.docIndex)} (p. ${pl.page})` : String(pl.page)));
      if (order.length === 1 && list.length === 1) {
        st.line = multiDoc ? `Found in ${pageWords[0]}` : `Found on page ${pageWords[0]}`;
      } else if (order.length === 1) {
        st.line = `Found ${list.length} times, all move together`;
      } else if (multiDoc) {
        st.line = `Found in ${pageWords.join(', ')}`;
      } else {
        st.line = `Found on pages ${pageWords.join(', ')} (${list.length} places)`;
      }

      if (serverAnchorsHere(row).length) {
        st.state = 'here';
        st.badge = PLACEMENT_COPY.badgeHere;
        return st;
      }
      const first = order[0];
      st.jump = { docIndex: first.docIndex, page: first.page };
      st.state = 'elsewhere';
      if (first.docIndex !== docIndex) {
        st.badge = PLACEMENT_COPY.badgeOtherDoc;
        st.badgeTitle = docTitle(first.docIndex);
      } else {
        st.badge = `On page ${first.page}`;
      }
      return st;
    },
    [pageCount, pageNum, docIndex, anyPreviewable, localAnchorsHere, serverIsFresh, serverAnchors, docs, docTitle, serverAnchorsHere],
  );

  // ---- Edits -----------------------------------------------------------------
  const updateRow = useCallback((key: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const dirty = useMemo(() => JSON.stringify(rows) !== JSON.stringify(savedRows), [rows, savedRows]);

  const endPick = useCallback(() => setPickKey(null), []);

  const goToPage = useCallback(
    (n: number) => {
      if (n < 1 || n > pageCount) return;
      setPageNum(n);
    },
    [pageCount],
  );

  const showDoc = useCallback(
    (index: number, page = 1) => {
      setPickKey(null);
      if (index !== docIndex) setDocIndex(index);
      setPageNum(page);
    },
    [docIndex],
  );

  const jumpTo = useCallback(
    (jump: { docIndex: number; page: number } | null) => {
      if (!jump) return;
      if (jump.docIndex !== docIndex) showDoc(jump.docIndex, jump.page);
      else goToPage(jump.page);
    },
    [docIndex, showDoc, goToPage],
  );

  const jumpToFirstMatch = useCallback(
    (row: Row) => {
      if (effectiveAnchorsHere(row).length) return;
      const list = serverIsFresh(row) ? (serverAnchors.hits[row.key] ?? []) : [];
      if (!list.length) return;
      jumpTo({ docIndex: list[0].docIndex, page: list[0].page });
    },
    [effectiveAnchorsHere, serverIsFresh, serverAnchors, jumpTo],
  );

  const addField = () => {
    const key = `n${++newKeySeq.current}`;
    const row: Row = {
      key,
      id: null,
      title: 'New field',
      tabKeywords: '',
      keywordType: 'signature',
      signatureX: '0',
      signatureY: '0',
      signaturePageNum: '',
      width: '50',
      optional: false,
    };
    setRows((prev) => [...prev, row]);
    setSelectedKey(key);
    setPickKey(null);
    focusWordsNext.current = true;
  };

  useEffect(() => {
    if (focusWordsNext.current && wordsInputRef.current) {
      focusWordsNext.current = false;
      wordsInputRef.current.focus();
    }
  });

  const removeField = (row: Row) => {
    setAsk({
      title: PLACEMENT_COPY.removeTitle,
      message: PLACEMENT_COPY.removeConfirm,
      confirmLabel: PLACEMENT_COPY.removeAction,
      variant: 'danger',
      onConfirm: () => {
        const remaining = rows.filter((r) => r.key !== row.key);
        setRows(remaining);
        if (pickKey === row.key) setPickKey(null);
        if (selectedKey === row.key) setSelectedKey(remaining[0]?.key ?? null);
      },
    });
  };

  /** Why a fixed spot cannot be set on the document shown, in words the reader can act on. */
  const fixedSpotBlockedReason = (): string => {
    if (isPacket) return PLACEMENT_COPY.fixedNotInPackets;
    const doc = currentDoc;
    if (!doc) return PLACEMENT_COPY.fixedNeedsPreview;
    // previewable covers Word too (it is converted for viewing), so it cannot stand in for "this is a PDF".
    if (!isPdfName(doc.fileName)) return PLACEMENT_COPY.fixedNeedsPdf;
    if (!doc.previewable || pdfFailed) return PLACEMENT_COPY.fixedNeedsPreview;
    return '';
  };

  const setPlacementMode = (row: Row, mode: string) => {
    setPlaceMessage(null);
    if (mode === 'fixed') {
      if (isFixed(row)) return;
      const reason = fixedSpotBlockedReason();
      if (reason) {
        setPlaceMessage({ key: row.key, text: reason });
        return;
      }
      if (!pageInfo) return; // the page is still being drawn
      const info = pageInfo;
      // Run now, or once the question below is answered yes.
      const toFixed = () => {
        const already = rows.filter((o) => o.key !== row.key && isFixed(o) && pageNumberOf(o.signaturePageNum) === pageNum).length;
        const seed = freePlaceDefaultOffsetPt(info.widthMm, info.heightMm, already);
        updateRow(row.key, {
          tabKeywords: '',
          signaturePageNum: String(pageNum),
          signatureX: String(seed.xPt),
          signatureY: String(seed.yPt),
        });
        setPickKey(null);
        setSelectedKey(row.key);
      };
      if (phpTrim(row.tabKeywords) !== '') {
        setAsk({
          title: PLACEMENT_COPY.fixedClearsWordsTitle,
          message: PLACEMENT_COPY.fixedClearsWords.replace('{page}', String(pageNum)),
          confirmLabel: PLACEMENT_COPY.fixedClearsWordsAction,
          variant: 'warning',
          onConfirm: toFixed,
        });
        return;
      }
      toFixed();
    } else {
      if (!isFixed(row)) return;
      updateRow(row.key, { signaturePageNum: '', signatureX: '0', signatureY: '0' });
      focusWordsNext.current = true;
    }
    setSelectedKey(row.key);
  };

  const togglePick = (row: Row) => {
    if (pickKey === row.key) {
      setPickKey(null);
      return;
    }
    if (!currentDoc?.previewable || !currentDoc.key) return;
    setPickKey(row.key);
    setSelectedKey(row.key);
  };

  const applyPick = (rowKey: string, text: string) => {
    updateRow(rowKey, { tabKeywords: text });
    setPickKey(null);
  };

  // ---- Dragging and resizing -------------------------------------------------
  const startDrag = (e: ReactMouseEvent, kind: 'move' | 'resize', row: Row, index: number) => {
    if (e.button !== 0 || pickKey) return;
    // readOnly: a press on an element only selects its field.
    if (readOnly) {
      setSelectedKey(row.key);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setSelectedKey(row.key);
    dragRef.current = {
      kind,
      key: row.key,
      index,
      clientX: e.clientX,
      clientY: e.clientY,
      startX: offsetValue(row.signatureX),
      startY: offsetValue(row.signatureY),
      startW: signatureWidthMm(row.width),
    };
    setLive({ kind, key: row.key, index, dxPx: 0, dyPx: 0 });
  };

  const commitDrag = useCallback(
    (d: DragStart, dxPx: number, dyPx: number) => {
      if (dxPx === 0 && dyPx === 0) return;
      if (d.kind === 'move') {
        updateRow(d.key, {
          signatureX: String(roundOffsetPt(d.startX + dragDeltaPxToPt(dxPx, renderWidthPx))),
          signatureY: String(roundOffsetPt(d.startY + dragDeltaPxToPt(dyPx, renderWidthPx))),
        });
      } else {
        updateRow(d.key, { width: String(roundWidthMm(Math.max(MIN_RESIZE_MM, d.startW + pxToMm(dxPx, renderWidthPx)))) });
      }
    },
    [renderWidthPx, updateRow],
  );

  const dragging = live !== null;
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setLive({ kind: d.kind, key: d.key, index: d.index, dxPx: e.clientX - d.clientX, dyPx: d.kind === 'move' ? e.clientY - d.clientY : 0 });
    };
    const onUp = (e: MouseEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      setLive(null);
      if (d) commitDrag(d, e.clientX - d.clientX, d.kind === 'move' ? e.clientY - d.clientY : 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, commitDrag]);

  const liveOffsets = (row: Row): { x: number; y: number; w: number } => {
    const x = offsetValue(row.signatureX);
    const y = offsetValue(row.signatureY);
    const w = signatureWidthMm(row.width);
    if (!live || live.key !== row.key) return { x: Math.round(x), y: Math.round(y), w };
    if (live.kind === 'move') {
      return {
        x: roundOffsetPt(x + dragDeltaPxToPt(live.dxPx, renderWidthPx)),
        y: roundOffsetPt(y + dragDeltaPxToPt(live.dyPx, renderWidthPx)),
        w,
      };
    }
    return { x: Math.round(x), y: Math.round(y), w: roundWidthMm(Math.max(MIN_RESIZE_MM, w + pxToMm(live.dxPx, renderWidthPx))) };
  };

  // ---- Keyboard: arrows nudge the selected field ----------------------------
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  useEffect(() => {
    keyHandlerRef.current = (e: KeyboardEvent) => {
      // A question is on top (ConfirmDialog): its own Modal answers the keys.
      if (ask) return;
      // Escape, as corp's onEscapeKeyDown: ends pick mode, else closes (asking
      // first when there are changes). Here because the Modal ignores Escape.
      if (e.key === 'Escape') {
        if (pickKey) setPickKey(null);
        else handleOpenChange(false);
        return;
      }
      // readOnly: nothing moves.
      if (readOnly) return;
      const steps: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const dir = steps[e.key];
      if (!dir || !selectedKey || live) return;
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || active?.isContentEditable) return;
      const row = rows.find((r) => r.key === selectedKey);
      if (!row || !effectiveAnchorsHere(row).length) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      updateRow(row.key, {
        signatureX: String(roundOffsetPt(offsetValue(row.signatureX) + dir[0] * step)),
        signatureY: String(roundOffsetPt(offsetValue(row.signatureY) + dir[1] * step)),
      });
    };
  });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => keyHandlerRef.current(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // ---- Close and save --------------------------------------------------------
  const handleOpenChange = (next: boolean) => {
    // readOnly: nothing was changed, so closing never asks.
    if (!next && dirty && !saving && !readOnly) {
      setAsk({
        title: PLACEMENT_COPY.leaveTitle,
        message: isPacket ? PLACEMENT_COPY.leavePacket : PLACEMENT_COPY.leaveDocument,
        confirmLabel: PLACEMENT_COPY.leaveAction,
        variant: 'warning',
        onConfirm: () => onOpenChange(next),
      });
      return;
    }
    onOpenChange(next);
  };

  const revertAll = () => {
    setRows(savedRows);
    setPickKey(null);
    setPlaceMessage(null);
    setSaveError(null);
    if (!savedRows.some((r) => r.key === selectedKey)) setSelectedKey(savedRows[0]?.key ?? null);
  };

  const handleSave = async () => {
    const session = sessionRef.current;
    if (!session) return;
    const { signal } = session;
    const keywords = rows.map(rowToInput);
    const surviving = new Set(rows.map((r) => r.id).filter((id): id is number => !!id));
    const deleted = loadedIds.filter((id) => !surviving.has(id));
    setSaving(true);
    setSaveError(null);
    try {
      const res = await cancellable(signal, () => saveEsignFields(ownerType, ownerId, keywords, deleted, isPacket ? undefined : fileName));
      if (!res.ok) {
        setSaveError(res.message);
        const m = /^keywords\.(\d+)\./.exec(res.field ?? '');
        const named = m ? rows[parseInt(m[1], 10)] : undefined;
        if (named) setSelectedKey(named.key);
        return;
      }
      const stored = res.data?.keywords ?? [];
      const storedRows = stored.map(rowFromField);
      setRows(storedRows);
      setSavedRows(storedRows);
      onSaved(stored);
      onOpenChange(false);
    } catch (error) {
      if (isAbortError(error)) return;
      setSaveError(PLACEMENT_COPY.saveFailed);
    } finally {
      if (!signal.aborted) setSaving(false);
    }
  };

  // ---- Rendering helpers -----------------------------------------------------
  const selectedRow = rows.find((r) => r.key === selectedKey) ?? null;
  const selectedStatus = selectedRow ? statusFor(selectedRow) : null;

  const pickRuns: TextRun[] | null = useMemo(() => {
    if (runsHere === null) return null;
    if (runsHere.length) return runsHere;
    // No server runs for this page: fall back to what pdf.js reads (best effort).
    return (itemsHere ?? []).map((it) => ({ xPt: it.transform[4], yPt: it.transform[5], text: phpTrim(it.str) })).filter((r) => r.text !== '');
  }, [runsHere, itemsHere]);

  const docMessage = (doc: PreviewDoc): string => {
    switch (doc.reason) {
      case 'nofile':
        if (isPacket) return PLACEMENT_COPY.entryNoFile;
        return (doc.fileName ?? '').trim() === '' ? PLACEMENT_COPY.noFile : PLACEMENT_COPY.missingFile;
      case 'unsupported':
        return PLACEMENT_COPY.unsupported;
      case 'unreadable':
        return PLACEMENT_COPY.unreadable;
      case 'unavailable':
        return PLACEMENT_COPY.unavailable;
      case 'pending':
        return PLACEMENT_COPY.preparing;
      default:
        return PLACEMENT_COPY.convert;
    }
  };

  let stageMessage: { text: string; bad?: boolean } | null = null;
  if (loading) stageMessage = { text: PLACEMENT_COPY.preparing };
  else if (previewError) stageMessage = { text: previewError, bad: true };
  else if (currentDocError) stageMessage = { text: currentDocError, bad: true };
  else if (docs.length === 0) stageMessage = isPacket ? { text: PLACEMENT_COPY.noDocs } : { text: PLACEMENT_COPY.noFile };
  else if (currentDoc && !currentDoc.previewable) stageMessage = { text: docMessage(currentDoc) };
  else if (pdfFailed) stageMessage = { text: PLACEMENT_COPY.convert, bad: true };
  else if (!pdf || !pageInfo) stageMessage = { text: PLACEMENT_COPY.preparing };

  const renderOverlay = () => {
    if (!pageInfo || !pdf) return null;
    const nodes: ReactNode[] = [];

    if (pickKey && pickRuns) {
      const pickRow = rows.find((r) => r.key === pickKey);
      const colour = fieldColour(rows.findIndex((r) => r.key === pickKey));
      if (pickRow) {
        pickRuns.forEach((run, i) => {
          const rect = runRectPx(geometryForRun(run, itemsHere), pageInfo, renderWidthPx);
          nodes.push(
            <button
              key={`pick-${i}`}
              type="button"
              data-testid="esign-pickable"
              aria-label={`Use “${run.text}”`}
              title={run.text}
              // corp's `hover:bg-current/20` is Tailwind 4 (a color-mix of currentColor); spelled out for Tailwind 3.
              className="absolute rounded-sm border bg-transparent hover:bg-[color-mix(in_oklab,currentColor_20%,transparent)]"
              style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderColor: colour, color: colour }}
              onClick={(e) => {
                e.stopPropagation();
                applyPick(pickRow.key, run.text);
              }}
            />,
          );
        });
      }
    }

    rows.forEach((row, rowIndex) => {
      const anchors = effectiveAnchorsHere(row);
      if (!anchors.length) return;
      const colour = fieldColour(rowIndex);
      const selected = row.key === selectedKey;
      const type = row.keywordType || 'signature';
      const sig = isSignatureType(type);
      const xOff = offsetValue(row.signatureX);
      const yOff = offsetValue(row.signatureY);
      const lv = live && live.key === row.key ? live : null;
      const offsets = liveOffsets(row);
      const size = elementSizeMm(type, lv?.kind === 'resize' ? offsets.w : row.width);
      const title = displayTitle(row);

      anchors.forEach((a, index) => {
        const p = placementMm(a.xPt, a.yPt, xOff, yOff, pageInfo.widthMm, pageInfo.heightMm);
        const ax = p.anchorX * px;
        const ay = p.anchorY * px;
        const left = p.x * px + (lv?.kind === 'move' ? lv.dxPx : 0);
        const top = p.y * px + (lv?.kind === 'move' ? lv.dyPx : 0);

        if (!a.free && !pickKey) {
          const rect = runRectPx(geometryForRun(a, itemsHere), pageInfo, renderWidthPx);
          nodes.push(
            <div
              key={`hl-${row.key}-${index}`}
              data-testid="esign-highlight"
              data-key={row.key}
              title={a.text}
              className="pointer-events-none absolute rounded-sm"
              style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, background: colourAlpha(colour, selected ? 0.35 : 0.18) }}
            />,
          );
        }

        if (!a.free && !pickKey) {
          const dx = left - ax;
          const dy = top - ay;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len >= TETHER_MIN_PX) {
            nodes.push(
              <div
                key={`tether-${row.key}-${index}`}
                data-testid="esign-tether"
                className="pointer-events-none absolute h-0 border-t border-dashed opacity-80"
                style={{
                  left: ax,
                  top: ay,
                  width: len,
                  borderColor: colour,
                  transformOrigin: '0 0',
                  transform: `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI}deg)`,
                }}
              />,
            );
          }
        }

        const lineH = TEXT_LINE_MM * px;
        const boxStyle: CSSProperties = sig
          ? { left, top, width: (size.widthMm ?? 0) * px, height: size.heightMm * px, color: colour }
          : { left, top, height: lineH, color: colour };

        nodes.push(
          <div
            key={`box-${row.key}-${index}`}
            data-testid="esign-box"
            data-key={row.key}
            data-type={type}
            role="button"
            tabIndex={-1}
            // readOnly: the element does not move, so it neither says so nor shows the move cursor.
            aria-label={readOnly ? title : `${title}, drag to move`}
            aria-pressed={selected}
            className={`absolute box-border ${readOnly ? 'cursor-pointer' : 'cursor-move'} select-none whitespace-nowrap border-[1.5px] ${selected ? 'border-solid shadow-[0_0_0_2px_rgba(255,255,255,0.9),0_0_0_4px_currentColor]' : 'border-dashed'} ${pickKey ? 'pointer-events-none opacity-40' : ''}`}
            style={{ ...boxStyle, background: 'rgba(255,255,255,0.35)' }}
            onMouseDown={(e) => startDrag(e, 'move', row, index)}
          >
            {sig ? (
              <span
                className="pointer-events-none flex h-full w-full items-center overflow-hidden pl-1 font-serif italic text-gray-700"
                style={{ fontSize: Math.max(8, size.heightMm * px * 0.55) }}
              >
                {type === 'initials' ? 'JD' : 'Jane Doe'}
              </span>
            ) : (
              <span
                className="pointer-events-none font-bold text-black"
                style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: TEXT_FONT_MM * px, lineHeight: `${lineH}px` }}
              >
                {(SAMPLE_TEXT[type] ?? SAMPLE_TEXT.shorttext)()}
              </span>
            )}
            <div
              className="pointer-events-none absolute bottom-full left-[-1.5px] flex min-h-4 items-center gap-1 rounded-t px-1.5 text-[11px] text-white"
              style={{ background: colour }}
            >
              <span aria-hidden="true">⣿</span>
              <span>{title}</span>
            </div>
            {/* readOnly: no resize handle. */}
            {sig && !readOnly && (
              <div
                data-testid="esign-resize"
                aria-hidden="true"
                className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-white/40"
                onMouseDown={(e) => startDrag(e, 'resize', row, index)}
              />
            )}
            {lv && lv.index === index && (
              <div
                data-testid="esign-readout"
                className="pointer-events-none absolute left-full top-[-18px] ml-1.5 rounded bg-gray-900 px-1.5 py-px text-[11px] text-white"
              >
                {lv.kind === 'resize' ? `Width ${offsets.w} mm` : `X ${offsets.x} · Y ${offsets.y}`}
              </div>
            )}
          </div>,
        );
      });
    });
    return nodes;
  };

  const typeOptions = (current: string): ReadonlyArray<{ value: string; label: string }> =>
    FIELD_TYPE_OPTIONS.some((o) => o.value === current) ? FIELD_TYPE_OPTIONS : [{ value: current, label: fieldTypeLabel(current) }, ...FIELD_TYPE_OPTIONS];

  const renderCard = (row: Row, rowIndex: number) => {
    const colour = fieldColour(rowIndex);
    const selected = row.key === selectedKey;
    const st = statusFor(row);
    const fixed = isFixed(row);
    const sig = isSignatureType(row.keywordType || 'signature');
    const metrics = liveOffsets(row);
    const editOpen = !!editing[row.key];
    const idBase = `esign-${row.key}`;

    const badge = st.badge ? (
      st.jump ? (
        <button
          type="button"
          className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 underline-offset-2 hover:underline"
          title={st.badgeTitle || undefined}
          data-testid="esign-badge"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedKey(row.key);
            jumpTo(st.jump);
          }}
        >
          {st.badge}
        </button>
      ) : (
        <span
          data-testid="esign-badge"
          title={st.badgeTitle || undefined}
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
            st.state === 'notfound' || st.state === 'fixedmissing' || st.state === 'notext' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {st.badge}
        </span>
      )
    ) : null;

    if (!selected) {
      return (
        <li key={row.key}>
          <button
            type="button"
            data-testid="esign-field"
            data-key={row.key}
            className="flex w-full items-center justify-between gap-2 rounded border border-l-4 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50"
            style={{ borderLeftColor: colour }}
            onClick={() => {
              setSelectedKey(row.key);
              setPlaceMessage(null);
              if (pickKey) setPickKey(null);
            }}
          >
            <span className="truncate font-medium">{displayTitle(row)}</span>
            {st.badge && <span className="shrink-0 text-xs text-gray-500">{st.badge}</span>}
          </button>
        </li>
      );
    }

    return (
      <li
        key={row.key}
        data-testid="esign-field"
        data-key={row.key}
        data-selected="true"
        data-state={st.state}
        // corp's `shadow-sm` is Tailwind 4; Tailwind 3's `shadow` is the same shadow.
        className="space-y-2 rounded border border-l-4 bg-white px-3 py-2 text-sm shadow"
        style={{ borderLeftColor: colour }}
      >
        <div className="flex items-center gap-2">
          <Input
            aria-label="Field title"
            value={row.title}
            onChange={(e) => updateRow(row.key, { title: e.target.value })}
            className="h-8"
            disabled={readOnly}
          />
          {/* readOnly: no Remove. */}
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-red-600"
              leftIcon={<Trash2 className="mr-1 h-4 w-4" />}
              onClick={() => removeField(row)}
            >
              Remove field
            </Button>
          )}
        </div>

        <label className="block space-y-1">
          <span className="text-xs text-gray-600">{PLACEMENT_COPY.placeLabel}</span>
          <select className={selectClass} value={fixed ? 'fixed' : 'words'} onChange={(e) => setPlacementMode(row, e.target.value)} disabled={readOnly}>
            <option value="words">{PLACEMENT_COPY.placeByWords}</option>
            <option value="fixed">{PLACEMENT_COPY.placeFixed}</option>
          </select>
        </label>
        {placeMessage && placeMessage.key === row.key && (
          <p role="alert" className="text-xs text-amber-800">
            {placeMessage.text}
          </p>
        )}

        {!fixed && (
          <div className="space-y-1">
            <label htmlFor={`${idBase}-words`} className="text-xs text-gray-600">
              {PLACEMENT_COPY.wordsLabel}
            </label>
            <div className="flex gap-2">
              <Input
                id={`${idBase}-words`}
                ref={wordsInputRef}
                className="h-8"
                placeholder={PLACEMENT_COPY.wordsPlaceholder}
                value={row.tabKeywords}
                onChange={(e) => updateRow(row.key, { tabKeywords: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    jumpToFirstMatch(row);
                  }
                }}
                disabled={readOnly}
              />
              {/* readOnly: no Pick from document. */}
              {!readOnly && (
                <Button
                  type="button"
                  variant={pickKey === row.key ? 'primary' : 'secondary'}
                  size="sm"
                  className="h-8 shrink-0"
                  aria-pressed={pickKey === row.key}
                  disabled={!currentDoc?.previewable || !currentDoc.key}
                  leftIcon={<MousePointerClick className="mr-1 h-4 w-4" />}
                  onClick={() => togglePick(row)}
                >
                  {PLACEMENT_COPY.pick}
                </Button>
              )}
            </div>
          </div>
        )}

        {(badge || st.line) && (
          <div className="flex flex-wrap items-center gap-2">
            {badge}
            {st.line && <span className="text-xs text-gray-600">{st.line}</span>}
          </div>
        )}
        {(st.warns.length > 0 || st.state === 'fixedmissing') && (
          <ul className="space-y-0.5 text-xs text-amber-800" data-testid="esign-warnings">
            {st.warns.map((w) => (
              <li key={w}>{w === 'shortcode' ? PLACEMENT_COPY.warnShortcode : PLACEMENT_COPY.warnWhitespace}</li>
            ))}
            {st.state === 'fixedmissing' && <li>{st.banner}</li>}
          </ul>
        )}

        {/* grid-cols-1 base: this repo's lint wants a breakpoint-prefixed multi-column grid. */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs text-gray-600">Type</span>
            <select
              className={selectClass}
              value={row.keywordType}
              onChange={(e) => updateRow(row.key, { keywordType: e.target.value })}
              disabled={readOnly}
            >
              {typeOptions(row.keywordType).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-gray-600">Required</span>
            <select
              className={selectClass}
              value={row.optional ? 'no' : 'yes'}
              onChange={(e) => updateRow(row.key, { optional: e.target.value === 'no' })}
              disabled={readOnly}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-600" data-testid="esign-metrics">
            {[sig ? `Width ${metrics.w} mm` : null, `X ${metrics.x}`, `Y ${metrics.y}`].filter(Boolean).join(' · ')}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-7 px-2 text-xs"
            aria-pressed={editOpen}
            onClick={() => setEditing((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
          >
            Edit
          </Button>
        </div>
        {editOpen && (
          <div className={`grid grid-cols-1 gap-2 ${sig ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {sig && (
              <label className="block space-y-1">
                <span className="text-xs text-gray-600">Width (mm)</span>
                <Input
                  className="h-8"
                  inputMode="numeric"
                  value={row.width}
                  onChange={(e) => updateRow(row.key, { width: e.target.value })}
                  disabled={readOnly}
                />
              </label>
            )}
            <label className="block space-y-1">
              <span className="text-xs text-gray-600">X offset</span>
              <Input
                className="h-8"
                inputMode="decimal"
                value={row.signatureX}
                onChange={(e) => updateRow(row.key, { signatureX: e.target.value })}
                disabled={readOnly}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-gray-600">Y offset</span>
              <Input
                className="h-8"
                inputMode="decimal"
                value={row.signatureY}
                onChange={(e) => updateRow(row.key, { signatureY: e.target.value })}
                disabled={readOnly}
              />
            </label>
          </div>
        )}
      </li>
    );
  };

  const pickBarText = pickKey
    ? pickRuns === null
      ? PLACEMENT_COPY.pickBarLoading
      : pickRuns.length
        ? PLACEMENT_COPY.pickBar
        : PLACEMENT_COPY.pickBarEmpty
    : '';

  const showPdfStage = !stageMessage && pdf && pageInfo;

  return (
    <Modal
      isOpen={open}
      onClose={() => handleOpenChange(false)}
      // Escape is handled with the arrow keys above: every open Modal hears the key,
      // and this one sits on top of the document / packet modal and under its own questions.
      closeOnEscape={false}
      size="full"
      // corp's DialogContent: h-[94vh] max-h-[94vh] w-[calc(100vw-2rem)] max-w-[1400px] (from md up; below it Prospera's phone sheet).
      panelClassName="md:h-[94vh] md:max-h-[94vh] md:w-[calc(100vw-2rem)] md:max-w-[1400px]"
      // corp's DialogContent pads 24px across and nothing between header and footer; the markup's -mx-6 relies on that.
      bodyClassName="flex min-h-0 flex-col px-6 py-0 md:px-6"
      title={
        <>
          <DialogHeading title={`Signature fields: ${ownerTitle}`} />
          <p className="sr-only">Place each signature field on the document.</p>
        </>
      }
      footer={
        <div className="flex w-full items-center gap-3">
          {dirty && (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-sm text-blue-700">
                <AlertTriangle className="h-4 w-4" />
                Unsaved changes
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
                leftIcon={<RotateCcw className="mr-1 h-3.5 w-3.5" />}
                onClick={revertAll}
              >
                Revert
              </Button>
            </div>
          )}
          <div className="flex-1" />
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
          {/* readOnly: Close only. */}
          {!readOnly && (
            <Button onClick={handleSave} disabled={saving || loading || !!loadError || !dirty}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      }
    >
      {loadError ? (
        <div role="alert" className="flex-1 py-6 text-sm text-red-700">
          {loadError}
        </div>
      ) : (
        <div className="-mx-6 flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm">
              {docs.length > 1 && (
                <select
                  aria-label="Document"
                  className={`${selectClass} w-auto max-w-[320px]`}
                  value={docIndex}
                  onChange={(e) => showDoc(parseInt(e.target.value, 10) || 0)}
                >
                  {docs.map((d, i) => (
                    <option key={i} value={i}>
                      {(d.title || `Document ${i + 1}`) + (d.previewable || d.reason === 'pending' ? '' : ' (no preview)')}
                    </option>
                  ))}
                </select>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={pageNum <= 1 || !pdf}
                leftIcon={<ChevronLeft className="mr-1 h-4 w-4" />}
                onClick={() => goToPage(pageNum - 1)}
              >
                Previous page
              </Button>
              <span data-testid="esign-page-label">
                Page {pageNum} of {pageCount || 1}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={pageNum >= pageCount || !pdf}
                rightIcon={<ChevronRight className="ml-1 h-4 w-4" />}
                onClick={() => goToPage(pageNum + 1)}
              >
                Next page
              </Button>
              {statusMessage && <span className="text-xs text-gray-600">{statusMessage}</span>}
            </div>
            {pagesFromViewer && (
              <div role="alert" className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                {PLACEMENT_COPY.cannotBeSigned}
              </div>
            )}
            {selectedStatus?.banner && (
              <div data-testid="esign-banner" className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                {selectedStatus.banner}
              </div>
            )}
            <div ref={stageRef} className="relative min-h-0 flex-1 overflow-auto bg-gray-100 p-3">
              {pickKey && (
                <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 rounded bg-gray-900 px-3 py-1.5 text-sm text-white" data-testid="esign-pickbar">
                  <span className="flex-1">{pickBarText}</span>
                  <Button type="button" size="sm" variant="secondary" className="h-7" leftIcon={<X className="mr-1 h-3.5 w-3.5" />} onClick={endPick}>
                    Done
                  </Button>
                </div>
              )}
              {stageMessage && (
                <div className={`flex items-center gap-2 p-6 text-sm ${stageMessage.bad ? 'text-red-700' : 'text-gray-600'}`} data-testid="esign-stage-message">
                  {stageMessage.text === PLACEMENT_COPY.preparing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {stageMessage.text}
                </div>
              )}
              {showPdfStage && (
                <div className="relative mx-auto shadow" style={{ width: renderWidthPx, height: pageHeightPx }} data-testid="esign-page">
                  <PdfPageView pdf={pdf} pageNum={pageNum} widthPx={renderWidthPx} heightPx={pageHeightPx} />
                  <div
                    data-testid="esign-overlay"
                    className="absolute inset-0"
                    onMouseDown={(e) => {
                      if (e.target === e.currentTarget && pickKey) setPickKey(null);
                    }}
                  >
                    {renderOverlay()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* md:w-[360px]: this repo's lint refuses a bare fixed width of 300px or more. */}
          <div className="flex shrink-0 flex-col border-l bg-gray-50 md:w-[360px]">
            {/* readOnly: no Add field. */}
            {!readOnly && (
              <div className="flex justify-end border-b px-3 py-2">
                <Button type="button" variant="secondary" size="sm" onClick={addField} disabled={loading} leftIcon={<Plus className="mr-1 h-4 w-4" />}>
                  Add field
                </Button>
              </div>
            )}
            <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {!loading && rows.length === 0 && <li className="text-sm text-gray-500">{PLACEMENT_COPY.noRows}</li>}
              {rows.map((row, i) => renderCard(row, i))}
            </ul>
          </div>
        </div>
      )}

      {saveError && (
        <div role="alert" className="-mx-6 shrink-0 border-t border-red-200 bg-red-50 px-6 py-3 text-sm text-red-700">
          {saveError}
        </div>
      )}

      <ConfirmDialog
        isOpen={ask !== null}
        onClose={() => setAsk(null)}
        onConfirm={() => ask?.onConfirm()}
        title={ask?.title ?? ''}
        message={ask?.message ?? ''}
        confirmLabel={ask?.confirmLabel}
        variant={ask?.variant}
      />
    </Modal>
  );
}
